/**
 * SnapManager - Manages point snapping and constraints.
 *
 * Provides ortho mode (constrain to H/V), polar mode
 * (constrain to angular increments), and object snap
 * (snap to entity points like endpoints, midpoints, centers).
 *
 * Based on ODA OSnapManager pattern from:
 * C:\oda\vc17_amd64mt\Drawing\Examples\Editor\OSnapManager.cpp
 */

import type { DRDrawingViewer } from "../DRDrawingViewer";
import type { Point2D, Point3D } from "../interaction/types";
import { deleteObj } from "../core/delete";
import { getCurrentSpaceId } from "../core/oda";
import { OpenMode, SubentSelectionMode } from "../core/constants";
import type { DrawingModule } from "../types/DrawingWeb";
import {
  OSNAP_MODE_MAP,
  type ObjectSnapMode,
  type PolarAngleMode,
  type SnapConfig,
  type SnapResult,
} from "./types";
import { getEntityType, EntityType } from "../entities/detection";
import {
  getClosestPointToPolyline,
  getClosestPointToCircle,
  getClosestPointToArc,
  getClosestPointToEllipse,
} from "./nearest";
import { getPerpendicularPointsOnPolyline } from "./perpendicular/polyline";
import type { SnapOptions } from "./types";

/**
 * Polar angle presets for each mode.
 */
const POLAR_ANGLES: Record<PolarAngleMode, number[]> = {
  "90": [0, 90, 180, 270],
  "45": [0, 45, 90, 135, 180, 225, 270, 315],
  "30": [0, 30, 60, 90, 120, 150, 180, 210, 240, 270, 300, 330],
};

/**
 * Tolerance in degrees for polar snap.
 * Point must be within this angle of a snap angle to snap.
 */
const POLAR_TOLERANCE = 7.5;

/**
 * Default object snap aperture in pixels.
 * This is zoom-independent - measured in screen pixels.
 */
const DEFAULT_OSNAP_APERTURE = 20;

/**
 * Time budget for findObjectSnap in milliseconds (~half a frame at 60fps).
 * If processing takes longer, bail out with the best snap found so far.
 */
const SNAP_TIME_BUDGET_MS = 8;

/**
 * Maximum snap aperture as a fraction of the drawing diagonal.
 * Prevents select1() from selecting the entire drawing when zoomed out.
 */
const MAX_APERTURE_FRACTION = 0.05;

/**
 * Ratio of view field height to drawing diagonal above which all snapping is disabled.
 * When the user is very zoomed out (view shows many times the drawing extent),
 * snapping is meaningless and potentially disruptive.
 */
const SNAP_DISABLE_ZOOM_RATIO = 3.0;

export class SnapManager {
  private config: SnapConfig = {
    orthoEnabled: false,
    polarEnabled: false,
    polarAngleMode: "45",
    objectSnapEnabled: false,
    objectSnapModes: new Set(),
    objectSnapAperture: DEFAULT_OSNAP_APERTURE,
  };

  /** Entity handles to exclude from object snap (e.g. the entity being edited). */
  private excludedHandles = new Set<string>();

  // Persistent ODA objects reused across findObjectSnap calls (avoids alloc/dealloc per frame)
  private _pt1: DrawingModule.OdGePoint3d | null = null;
  private _pt2: DrawingModule.OdGePoint3d | null = null;
  private _pointArray: DrawingModule.OdGePoint3dArray | null = null;
  private _lastCursorPt: DrawingModule.OdGePoint3d | null = null;
  private _cursorPt: DrawingModule.OdGePoint3d | null = null;
  private _resultPt: DrawingModule.OdGePoint3d | null = null;

  // Cached drawing diagonal for aperture clamping
  private _drawingDiagonal: number = 0;

  constructor(private viewer: DRDrawingViewer) {}

  /**
   * Dispose persistent ODA objects. Must be called when the SnapManager is no longer needed.
   */
  dispose(): void {
    if (this._pt1) {
      deleteObj(this._pt1);
      this._pt1 = null;
    }
    if (this._pt2) {
      deleteObj(this._pt2);
      this._pt2 = null;
    }
    if (this._pointArray) {
      deleteObj(this._pointArray);
      this._pointArray = null;
    }
    if (this._lastCursorPt) {
      deleteObj(this._lastCursorPt);
      this._lastCursorPt = null;
    }
    if (this._cursorPt) {
      deleteObj(this._cursorPt);
      this._cursorPt = null;
    }
    if (this._resultPt) {
      deleteObj(this._resultPt);
      this._resultPt = null;
    }
  }

  /**
   * Process a point with snap/constraint logic.
   * Called by InteractionManager before passing coordinates to draggers.
   *
   * Priority order:
   * 1. Object snap (if enabled and within aperture)
   * 2. Ortho mode (if enabled)
   * 3. Polar mode (if enabled and within tolerance)
   *
   * @param world The raw world coordinates
   * @param lastPoint The last confirmed point (for constraints)
   * @param screen Screen coordinates (required for object snap aperture check)
   * @returns SnapResult with the processed point
   */
  processPoint(
    world: Point3D,
    lastPoint?: Point3D,
    screen?: Point2D,
    snapOptions: SnapOptions = {
      acceptOrtho: true,
      acceptPolar: true,
      acceptObject: true,
    },
  ): SnapResult {
    let point = world;
    let constrained = false;
    let constraintType: SnapResult["constraintType"] = null;
    let osnapMode: ObjectSnapMode | undefined;
    let polarAngle: number | undefined;
    let orthoAxis: "horizontal" | "vertical" | undefined;

    // Disable object snap when very zoomed out relative to drawing extent
    const diagonal = this.getDrawingDiagonal();
    let skipObjectSnap = false;
    if (diagonal > 0) {
      const gsView = this.viewer.app.getDevice().viewAt(0);
      const fieldHeight = gsView.fieldHeight();
      if (fieldHeight / diagonal > SNAP_DISABLE_ZOOM_RATIO) {
        skipObjectSnap = true;
      }
    }

    // 1. Object snap (highest priority)
    if (this.config.objectSnapEnabled && snapOptions.acceptObject) {
      const osnap = this.findObjectSnap(world, lastPoint);
      if (osnap) {
        point = osnap.point;
        constrained = true;
        constraintType = "osnap";
        osnapMode = osnap.mode;
      }
    }

    // 2. Apply ortho constraint if enabled and we have a reference point
    // Ortho takes precedence over polar
    if (
      !constrained &&
      this.config.orthoEnabled &&
      lastPoint &&
      snapOptions.acceptOrtho &&
      !skipObjectSnap
    ) {
      const orthoResult = this.orthoSnap(point, lastPoint);
      point = orthoResult.point;
      constrained = true;
      constraintType = "ortho";
      orthoAxis = orthoResult.axis;
    }
    // 3. Apply polar constraint if enabled (and ortho didn't already constrain)
    else if (
      !constrained &&
      this.config.polarEnabled &&
      lastPoint &&
      snapOptions.acceptPolar
    ) {
      const polarResult = this.polarSnap(point, lastPoint);
      if (polarResult.snapped) {
        point = polarResult.point;
        constrained = true;
        constraintType = "polar";
        polarAngle = polarResult.angle;
      }
    }
    return {
      point,
      constrained,
      constraintType,
      osnapMode,
      polarAngle,
      orthoAxis,
    };
  }

  // ==================== Object Snap ====================

  /**
   * Eagerly computes and caches the drawing-extents diagonal used for osnap aperture
   * clamping, so the one-time O(entity-count) WASM traversal happens during drawing
   * load instead of blocking the user's first drag gesture.
   */
  warmUp(): void {
    this.getDrawingDiagonal();
  }

  private getDrawingDiagonal(): number {
    if (this._drawingDiagonal > 0) return this._drawingDiagonal;

    const module = this.viewer.module;
    const db = this.viewer.database;
    if (!db) return 0;

    const currentSpaceId = getCurrentSpaceId(db);
    const currentSpace = module.OdDbBlockTableRecord.cast(
      currentSpaceId.safeOpenObject(OpenMode.kForRead, false),
    );
    if (!currentSpace) return 0;

    const extents = new module.OdGeExtents3d();
    currentSpace.getGeomExtents(extents);

    const min = extents.minPoint();
    const max = extents.maxPoint();

    const dx = max.x - min.x;
    const dy = max.y - min.y;
    this._drawingDiagonal = Math.sqrt(dx * dx + dy * dy);

    deleteObj(min);
    deleteObj(max);
    deleteObj(extents);
    deleteObj(currentSpace);

    return this._drawingDiagonal;
  }

  /**
   * Find the closest object snap point within the aperture.
   * Uses GPU-accelerated OdDbSelectionSet.select1() to find nearby entities,
   * then calls getOsnapPoints() only on those entities.
   *
   * This approach is much faster than iterating all entities in the drawing.
   * Pattern based on ODA OSnapManager.cpp which uses view selection for pre-filtering.
   *
   * @param worldPoint Cursor position in world coordinates
   * @param lastPoint Last confirmed point in the current drawing operation
   *   (e.g. the previously placed polyline vertex). Required for
   *   perpendicular/tangent snaps - those modes are skipped when absent,
   *   matching ODA's OSnapManager::checkSnapPoints() behavior.
   * @returns Snap result or null if no snap found
   */
  private findObjectSnap(
    worldPoint: Point3D,
    lastPoint?: Point3D,
  ): { point: Point3D; mode: ObjectSnapMode } | null {
    const module = this.viewer.module;
    const db = this.viewer.database;

    if (!db) {
      return null;
    }

    const aperture = this.config.objectSnapAperture;

    let closestSnap: { point: Point3D; mode: ObjectSnapMode } | null = null;

    // Convert aperture from screen pixels to world units
    let apertureWorld = this.viewer.screenDistanceToWorld(aperture);
    // Clamp aperture to a fraction of drawing extent to prevent select1() from selecting everything
    const diagonal = this.getDrawingDiagonal();
    if (diagonal > 0) {
      const maxAperture = diagonal * MAX_APERTURE_FRACTION;
      if (apertureWorld > maxAperture) {
        apertureWorld = maxAperture;
      }
    }

    // Use squared world distance to avoid sqrt in the inner loop
    let closestDistanceSq = apertureWorld * apertureWorld;

    // Lazily create persistent ODA objects on first use, then reuse across calls
    if (!this._pt1) {
      this._pt1 = new module.OdGePoint3d(0, 0, 0);
      this._pt2 = new module.OdGePoint3d(0, 0, 0);
      this._cursorPt = new module.OdGePoint3d(0, 0, 0);
      this._lastCursorPt = new module.OdGePoint3d(0, 0, 0);
      this._resultPt = new module.OdGePoint3d(0, 0, 0);
    }

    // Get world-to-eye matrix from active GsView (required for kOsModeNear on arcs, polylines, etc.)
    // ODA OSnapManager.cpp:493 uses pViewImpl->worldToEyeMatrix(), not identity.
    const gsDevice = this.viewer.app.getDevice();
    const gsView = gsDevice.viewAt(0);
    const xWorldToEye = gsView.viewingMatrix();

    // Update aperture box corners
    this._pt1.set(
      worldPoint.x - apertureWorld,
      worldPoint.y - apertureWorld,
      0,
    );
    this._pt2!.set(
      worldPoint.x + apertureWorld,
      worldPoint.y + apertureWorld,
      0,
    );

    // Rebuild point array with updated corners
    if (this._pointArray) deleteObj(this._pointArray);
    this._pointArray = new module.OdGePoint3dArray();
    this._pointArray!.push_back(this._pt1!);
    this._pointArray!.push_back(this._pt2!);

    // Update cursor position for nearest/perpendicular/tangent modes
    this._cursorPt!.set(worldPoint.x, worldPoint.y, worldPoint.z);

    // Reference point for perpendicular/tangent snaps (e.g. the previously
    // placed vertex while drawing a polyline). Fixed for the whole call -
    // perpendicular/tangent are skipped below when this isn't provided.
    if (lastPoint) {
      this._lastCursorPt!.set(lastPoint.x, lastPoint.y, lastPoint.z);
    }

    // Get active viewport for selection
    const vpId = db.activeViewportId();

    if (!vpId || vpId.isNull()) {
      deleteObj(xWorldToEye);
      return null;
    }

    // GPU crossing selection - returns only entities within aperture box
    const selection = module.OdDbSelectionSet.select1(
      vpId,
      2, // nPoints (2 corners = box)
      this._pointArray.asArrayPtr(),
      3, // kCrossing
      SubentSelectionMode.kDisableSubents,
      null as any,
    );
    if (!selection) {
      deleteObj(xWorldToEye);
      return null;
    }

    // Get selected entity IDs
    const entityIds = selection.objectIdArray();
    const entityCount = entityIds.size();

    const deadline = performance.now() + SNAP_TIME_BUDGET_MS;

    // Process only nearby entities (from GPU selection)
    for (let i = 0; i < entityCount; i++) {
      const entityId = entityIds.get(i);
      const entityObj = entityId.safeOpenObject(OpenMode.kForRead, false);
      deleteObj(entityId);

      if (entityObj) {
        // Must use ODA cast to get proper OdDbEntity with getOsnapPoints method
        const entity = module.OdDbEntity.cast(entityObj);
        if (!entity) {
          deleteObj(entityObj);
          continue;
        }

        // Skip hatches - they should not be snappable
        // Pattern from ODA OSnapManager.h isTargetEntity()
        const entityClass = entity.isA();
        if (entityClass.isEqualTo(module.OdDbHatch.desc())) {
          deleteObj(entityObj);
          continue;
        }

        // Skip excluded entities (e.g. entity being edited)
        if (this.excludedHandles.size > 0) {
          const handle = entity.getDbHandle();
          const handleStr = handle.ascii();
          const handleValue = handleStr.toString();
          deleteObj(handleStr);
          deleteObj(handle);
          if (this.excludedHandles.has(handleValue)) {
            deleteObj(entityObj);
            continue;
          }
        }

        // Try each enabled snap mode
        for (const modeName of this.config.objectSnapModes) {
          // Perpendicular/tangent need a real reference point - skip them
          // rather than matching against a meaningless fallback point.
          if (
            (modeName === "perpendicular" || modeName === "tangent") &&
            !lastPoint
          ) {
            continue;
          }

          const modeValue = OSNAP_MODE_MAP[modeName];

          const snapPoints = new module.OdGePoint3dArray();

          entity.getOsnapPoints(
            modeValue,
            0, // gsSelectionMark (0 = whole entity)
            this._cursorPt!,
            this._lastCursorPt!,
            xWorldToEye,
            snapPoints,
          );

          // For perpendicular on polylines: getOsnapPoints returns 0 points
          // in this WASM build (confirmed empirically), so fall back to a
          // custom per-segment calculation, same pattern as nearest below.
          if (modeName === "perpendicular" && snapPoints.size() === 0) {
            const entityType = getEntityType(module, entity.objectId());
            if (entityType === EntityType.Polyline && lastPoint) {
              const polyline = module.OdDbPolyline.cast(entityObj);
              if (polyline) {
                const candidates = getPerpendicularPointsOnPolyline(
                  module,
                  polyline,
                  lastPoint,
                );
                for (const pt of candidates) {
                  const dx = pt.x - worldPoint.x;
                  const dy = pt.y - worldPoint.y;
                  const distSq = dx * dx + dy * dy;
                  if (distSq < closestDistanceSq) {
                    closestDistanceSq = distSq;
                    closestSnap = { point: pt, mode: modeName };
                  }
                }
              }
            }
            deleteObj(snapPoints);
            continue;
          }

          // For nearest mode: use custom implementations for better accuracy.
          // getOsnapPoints returns incorrect results for ellipses, so always use custom method.
          if (modeName === "nearest") {
            const entityType = getEntityType(module, entity.objectId());
            const useCustomNearest =
              snapPoints.size() === 0 || entityType === EntityType.Ellipse;

            if (useCustomNearest) {
              let closestPt: DrawingModule.OdGePoint3d | null = null;

              switch (entityType) {
                case EntityType.Polyline: {
                  const polyline = module.OdDbPolyline.cast(entityObj);
                  if (polyline)
                    closestPt = getClosestPointToPolyline(
                      module,
                      polyline,
                      this._cursorPt!,
                    );
                  break;
                }
                case EntityType.Circle: {
                  const circle = module.OdDbCircle.cast(entityObj);
                  if (circle)
                    closestPt = getClosestPointToCircle(
                      module,
                      circle,
                      this._cursorPt!,
                    );
                  break;
                }
                case EntityType.Arc: {
                  const arc = module.OdDbArc.cast(entityObj);
                  if (arc)
                    closestPt = getClosestPointToArc(
                      module,
                      arc,
                      this._cursorPt!,
                    );
                  break;
                }
                case EntityType.Ellipse: {
                  const ellipse = module.OdDbEllipse.cast(entityObj);
                  if (ellipse)
                    closestPt = getClosestPointToEllipse(
                      module,
                      ellipse,
                      this._cursorPt!,
                    );
                  break;
                }
                default: {
                  // Fallback to generic curve
                  const curve = module.OdDbCurve.cast(entityObj);
                  if (curve) {
                    const result = curve.getClosestPointTo(
                      this._cursorPt!,
                      this._resultPt!,
                      false,
                    );
                    if (result === 0) closestPt = this._resultPt!;
                  }
                  break;
                }
              }

              if (closestPt) {
                // For custom nearest, the entity was already within aperture via select1()
                // Always accept the nearest point - it's the true closest point on the entity
                const dx = closestPt.x - worldPoint.x;
                const dy = closestPt.y - worldPoint.y;
                const distSq = dx * dx + dy * dy;

                // Accept if this is the first snap or closer than existing one
                if (!closestSnap || distSq < closestDistanceSq) {
                  closestDistanceSq = distSq;
                  closestSnap = {
                    point: { x: closestPt.x, y: closestPt.y, z: closestPt.z },
                    mode: modeName,
                  };
                }
                if (closestPt !== this._resultPt) deleteObj(closestPt);
              }
              deleteObj(snapPoints);
              continue; // Skip the normal distance loop for this mode
            }
          }

          // Check each snap point using world-space distance (avoids expensive worldToScreen per point)
          const snapCount = snapPoints.size();
          for (let j = 0; j < snapCount; j++) {
            const pt = snapPoints.get(j);
            const dx = pt.x - worldPoint.x;
            const dy = pt.y - worldPoint.y;
            const distSq = dx * dx + dy * dy;

            if (distSq < closestDistanceSq) {
              closestDistanceSq = distSq;
              closestSnap = {
                point: { x: pt.x, y: pt.y, z: pt.z },
                mode: modeName,
              };
            }
          }

          deleteObj(snapPoints);
        }

        deleteObj(entityObj);
      }

      // Time budget: bail out with best result so far
      if (performance.now() > deadline) break;
    }

    deleteObj(xWorldToEye);

    return closestSnap;
  }

  /**
   * Ortho snap - constrain point to horizontal or vertical axis.
   *
   * Algorithm from ODA OSnapManager.cpp lines 557-587:
   * - Calculate direction vector from lastPoint to point
   * - Use cross-product to find which axis (X or Y) is closest
   * - Constrain point to that axis
   *
   * @param point Current cursor position in world coordinates
   * @param lastPoint Reference point (last confirmed point)
   * @returns Constrained point
   */
  private orthoSnap(
    point: Point3D,
    lastPoint: Point3D,
  ): { point: Point3D; axis: "horizontal" | "vertical" } {
    const dx = point.x - lastPoint.x;
    const dy = point.y - lastPoint.y;

    // Calculate length of direction vector
    const len = Math.sqrt(dx * dx + dy * dy);

    // If points are essentially the same, return original point
    if (len < 1e-10) return { point, axis: "horizontal" };

    // Constrain to the axis we're closest to
    if (dx * dx < dy * dy) {
      // Closer to X axis - keep X movement, constrain Y and Z
      return {
        point: { x: lastPoint.x, y: point.y, z: lastPoint.z },
        axis: "vertical",
      };
    } else {
      // Closer to Y axis - keep Y movement, constrain X and Z
      return {
        point: { x: point.x, y: lastPoint.y, z: lastPoint.z },
        axis: "horizontal",
      };
    }
  }

  /**
   * Polar snap - constrain point to angular increments.
   *
   * Algorithm from ODA PolarSnapStrategy:
   * - Calculate angle from lastPoint to point
   * - Find closest snap angle based on current mode
   * - Only snap if within tolerance (5°)
   * - Preserve distance from lastPoint
   *
   * @param point Current cursor position in world coordinates
   * @param lastPoint Reference point (last confirmed point)
   * @returns Object with snapped point and whether snap occurred
   */
  private polarSnap(
    point: Point3D,
    lastPoint: Point3D,
  ): { point: Point3D; snapped: boolean; angle: number } {
    const dx = point.x - lastPoint.x;
    const dy = point.y - lastPoint.y;
    const distance = Math.sqrt(dx * dx + dy * dy);

    // If points are essentially the same, don't snap
    if (distance < 1e-10) {
      return { point, snapped: false, angle: 0 };
    }

    // Calculate current angle (0-360°)
    let currentAngle = (Math.atan2(dy, dx) * 180) / Math.PI;
    if (currentAngle < 0) currentAngle += 360;

    // Find closest snap angle
    const snapAngles = POLAR_ANGLES[this.config.polarAngleMode];
    let closestAngle = snapAngles[0];
    let minDiff = 360;

    for (const angle of snapAngles) {
      let diff = Math.abs(currentAngle - angle);
      // Handle wraparound at 360°/0°
      if (diff > 180) diff = 360 - diff;
      if (diff < minDiff) {
        minDiff = diff;
        closestAngle = angle;
      }
    }

    // Only snap if within tolerance
    if (minDiff > POLAR_TOLERANCE) {
      return { point, snapped: false, angle: currentAngle };
    }

    // Calculate snapped point preserving distance
    const radians = (closestAngle * Math.PI) / 180;
    return {
      point: {
        x: lastPoint.x + distance * Math.cos(radians),
        y: lastPoint.y + distance * Math.sin(radians),
        z: lastPoint.z,
      },
      snapped: true,
      angle: closestAngle,
    };
  }

  // ==================== Configuration ====================

  /**
   * Enable or disable ortho mode.
   */
  setOrtho(enabled: boolean): void {
    this.config.orthoEnabled = enabled;
  }

  /**
   * Check if ortho mode is enabled.
   */
  isOrthoEnabled(): boolean {
    return this.config.orthoEnabled;
  }

  /**
   * Toggle ortho mode.
   * @returns New ortho state
   */
  toggleOrtho(): boolean {
    this.config.orthoEnabled = !this.config.orthoEnabled;
    return this.config.orthoEnabled;
  }

  // ==================== Polar Configuration ====================

  /**
   * Enable or disable polar mode.
   */
  setPolar(enabled: boolean): void {
    this.config.polarEnabled = enabled;
  }

  /**
   * Check if polar mode is enabled.
   */
  isPolarEnabled(): boolean {
    return this.config.polarEnabled;
  }

  /**
   * Toggle polar mode.
   * @returns New polar state
   */
  togglePolar(): boolean {
    this.config.polarEnabled = !this.config.polarEnabled;
    return this.config.polarEnabled;
  }

  /**
   * Set the polar angle mode.
   * @param mode The angle mode ('90', '45', or '30')
   */
  setPolarAngleMode(mode: PolarAngleMode): void {
    this.config.polarAngleMode = mode;
  }

  /**
   * Get the current polar angle mode.
   */
  getPolarAngleMode(): PolarAngleMode {
    return this.config.polarAngleMode;
  }

  // ==================== Object Snap Configuration ====================

  /**
   * Enable or disable object snap.
   */
  setObjectSnap(enabled: boolean): void {
    this.config.objectSnapEnabled = enabled;
  }

  /**
   * Check if object snap is enabled.
   */
  isObjectSnapEnabled(): boolean {
    return this.config.objectSnapEnabled;
  }

  /**
   * Toggle object snap mode.
   * @returns New object snap state
   */
  toggleObjectSnap(): boolean {
    this.config.objectSnapEnabled = !this.config.objectSnapEnabled;
    return this.config.objectSnapEnabled;
  }

  /**
   * Enable a specific object snap mode.
   */
  enableSnapMode(mode: ObjectSnapMode): void {
    this.config.objectSnapModes.add(mode);
  }

  /**
   * Disable a specific object snap mode.
   */
  disableSnapMode(mode: ObjectSnapMode): void {
    this.config.objectSnapModes.delete(mode);
  }

  /**
   * Toggle a specific object snap mode.
   * @returns New state of the mode
   */
  toggleSnapMode(mode: ObjectSnapMode): boolean {
    if (this.config.objectSnapModes.has(mode)) {
      this.config.objectSnapModes.delete(mode);
      return false;
    } else {
      this.config.objectSnapModes.add(mode);
      return true;
    }
  }

  /**
   * Check if a specific snap mode is enabled.
   */
  isSnapModeEnabled(mode: ObjectSnapMode): boolean {
    return this.config.objectSnapModes.has(mode);
  }

  /**
   * Replace the entire set of enabled object snap modes.
   * Used to bulk-restore persisted settings on drawing open.
   */
  setSnapModes(modes: Set<ObjectSnapMode>): void {
    this.config.objectSnapModes = new Set(modes);
  }

  /**
   * Get all enabled snap modes.
   */
  getEnabledSnapModes(): ObjectSnapMode[] {
    return Array.from(this.config.objectSnapModes);
  }

  /**
   * Set the snap aperture (in pixels).
   */
  setSnapAperture(aperture: number): void {
    this.config.objectSnapAperture = Math.max(1, aperture);
  }

  /**
   * Get the snap aperture.
   */
  getSnapAperture(): number {
    return this.config.objectSnapAperture;
  }

  // ==================== Entity Exclusion ====================

  /**
   * Set entity handles to exclude from object snap.
   * Used to prevent snapping to the entity being edited.
   */
  setExcludedHandles(handles: string[]): void {
    this.excludedHandles = new Set(handles);
  }

  /**
   * Clear the excluded entity handles.
   */
  clearExcludedHandles(): void {
    this.excludedHandles.clear();
  }

  // ==================== General ====================

  /**
   * Get the current snap configuration.
   */
  getConfig(): Readonly<SnapConfig> {
    return {
      ...this.config,
      objectSnapModes: new Set(this.config.objectSnapModes),
    };
  }
}
